import type {
  CandidateProgress,
  CandidateSortField,
  CandidateStatus,
  DocumentStatus,
  DocumentsStatus,
  EmployeeTypeCode,
  OrphanReason,
  SigningParty,
  SigningPartyStatus,
  SigningSubmissionStatus,
  WorkflowStageKind,
  WorkflowStageStatus,
} from '@repo/contracts';

export { SYSTEM_ROLE_LABELS, systemRoleLabel } from '@repo/contracts';

export const EMPLOYEE_TYPE_LABELS: Record<EmployeeTypeCode, string> = {
  FT: 'Full-time',
  PT: 'Part-time',
  H1B: 'W2 Salaried Employee',
  EAD: 'W2 Hourly Employee',
  OPT: 'OPT',
  INTERN: 'Intern',
  C1099: 'Contractor (1099)',
  C2C: 'Corp-to-corp',
  IN_INTERN: 'India Interns',
  IN_CONTRACTOR: 'India Contractors',
};

export const CANDIDATE_STATUS_LABELS: Record<CandidateStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  on_hold: 'On hold',
  completed: 'Completed',
  cancelled: 'Withdrawn',
  hired: 'Hired',
};

type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

export const CANDIDATE_STATUS_TONES: Record<CandidateStatus, Tone> = {
  pending: 'warning',
  in_progress: 'info',
  on_hold: 'warning',
  completed: 'success',
  cancelled: 'neutral',
  hired: 'success',
};

export const CANDIDATE_PROGRESS_LABELS: Record<CandidateProgress, string> = {
  not_started: 'Not started',
  pending_registration: 'Pending registration',
  pending_forms: 'Pending forms',
  pending_documents: 'Pending documents',
  under_review: 'Under review',
  pending_hr: 'Pending HR',
  pending_signature: 'Pending signature',
  finished: 'Finished',
};

export const DOCUMENTS_STATUS_TONES: Record<DocumentsStatus, Tone> = {
  not_started: 'neutral',
  pending: 'warning',
  under_review: 'info',
  approved: 'success',
};

export const STAGE_KIND_LABELS: Record<WorkflowStageKind, string> = {
  self_registration: 'Self registration',
  document_upload: 'Document upload',
  form_submission: 'Form submission',
  signature_collection: 'Signature collection',
  hr_approval: 'HR approval',
  notification: 'Notification',
};

export const STAGE_STATUS_LABELS: Record<WorkflowStageStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  skipped: 'Skipped',
  completed: 'Completed',
};

export const STAGE_STATUS_TONES: Record<WorkflowStageStatus, Tone> = {
  pending: 'neutral',
  active: 'info',
  submitted: 'warning',
  approved: 'success',
  rejected: 'danger',
  skipped: 'neutral',
  completed: 'success',
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  pending: 'Pending',
  uploaded: 'Uploaded',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const DOCUMENT_STATUS_TONES: Record<DocumentStatus, Tone> = {
  pending: 'neutral',
  uploaded: 'warning',
  approved: 'success',
  rejected: 'danger',
};

export const CANDIDATE_SORT_LABELS: Record<CandidateSortField, string> = {
  fullName: 'Name',
  employeeType: 'Employee type',
  startDate: 'Start date',
  status: 'Status',
  departmentName: 'Department',
};

/** A status that can no longer be held, resumed or withdrawn, because the candidate's run is over. */
export const FINAL_CANDIDATE_STATUSES: readonly CandidateStatus[] = [
  'completed',
  'hired',
  'cancelled',
];

export const CANDIDATE_PAGE_SIZE = 25;
export const MAX_CANDIDATE_TAGS = 50;
export const MAX_CANDIDATE_TAG_LENGTH = 40;

export const SIGNING_STATUS_LABELS: Record<SigningSubmissionStatus, string> = {
  pending: 'Being sent',
  in_progress: 'Out for signature',
  completed: 'Signed',
  declined: 'Declined',
  expired: 'Expired',
  voided: 'Voided',
};

export const SIGNING_STATUS_TONES: Record<SigningSubmissionStatus, Tone> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  declined: 'danger',
  expired: 'danger',
  voided: 'neutral',
};

export const SIGNING_PARTY_LABELS: Record<SigningParty, string> = {
  hr: 'HR',
  candidate: 'Candidate',
};

export const SIGNING_PARTY_STATUS_LABELS: Record<SigningPartyStatus, string> = {
  awaiting: 'Waiting',
  sent: 'Sent',
  opened: 'Opened',
  completed: 'Signed',
  declined: 'Declined',
};

export const SIGNING_PARTY_STATUS_TONES: Record<SigningPartyStatus, Tone> = {
  awaiting: 'neutral',
  sent: 'info',
  opened: 'info',
  completed: 'success',
  declined: 'danger',
};

export const ORPHAN_REASON_LABELS: Record<OrphanReason, string> = {
  inactive: 'No longer active',
  no_hr_permission: 'No longer holds an HR role',
  unknown_user: 'Not found in the directory',
};
